// components/KeyPressGroup.tsx

import React, { useContext } from 'react';
import { GameLog } from '../../../Logs/LogsDisplay';
import KeyPressLog from './Events/KeyPressLog';
import LogEntry from './LogEntry';
import StackEvent from './Events/TimeOutEvents/StackEvent';
import './KeyPressGroup.css';
import { CombinedEventGroup } from './utils';
import TotalDamage from './TotalDamage';
import DurationDisplay from './DurationDisplay';
import { SimplifiedChampion } from '../../../../../gameTypes';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import ChampionCCGroup from './Events/ChampionCCGroup';
import ChampionStatChangeGroup from './Events/ChampionStatChangeGroup';
import ChampionDebuffGroup from './Events/ChampionDebuffGroup';
import RenderAggregatedDamageEvent from './Events/renderAggregatedDamageEvent';
import RenderAggregatedResourceEvent from './Events/RenderAggregatedResourceEvent';
import RenderAggregatedStatChangeEvent from './Events/RenderAggregatedStatChangeEvent';
import { aggregateRepeatedEvents } from './Events/aggregateRepeatedEvents';
import AggregatedStackEvent from './Events/AggregatedStackEvent';

interface KeyPressGroupProps {
  combinedGroup: CombinedEventGroup;
  spellImageUrls: Record<string, string>;
  AutoAttackImage: string;
  showStackingEvents: boolean;
  showStatEvents: boolean;
  showCCEvents: boolean;
  showDetails: boolean;
  championName: string;
  targetChampionName?: string;
  targetMaxHealth?: number;
}

const KeyPressGroup: React.FC<KeyPressGroupProps> = ({
  combinedGroup,
  spellImageUrls,
  AutoAttackImage,
  showStackingEvents,
  showStatEvents,
  showCCEvents,
  showDetails,
  championName,
  targetChampionName,
  targetMaxHealth,
}) => {
  const {
    keyPress,
    damageEvents,
    incomingDamageEvents,
    casterStatEvents,
    targetStatEventsByChampion,
    stackUpdates,
    casterCCEvents,
    targetCCEventsByChampion,
    targetDebuffEventsByChampion,
    // NEW FIELDS
    cooldownReductionEvents,
    damageMultiplierEvents
  } = combinedGroup;

  const { championsAssets } = useContext(GameAssetsContext);

  const casterAssets = championsAssets[championName];
  const casterImage = casterAssets?.championImageUrl;

  // Check for target or caster events
  const hasTargetEvents =
    Object.keys(targetStatEventsByChampion).length > 0 ||
    Object.keys(targetCCEventsByChampion).length > 0 ||
    Object.keys(targetDebuffEventsByChampion).length > 0;
  const hasCasterEvents =
    casterStatEvents.length > 0 || casterCCEvents.length > 0;

  const shouldShowStackDivider =
    showStackingEvents &&
    stackUpdates.length > 0 &&
    (damageEvents.length > 0 ||
      hasTargetEvents ||
      hasCasterEvents ||
      cooldownReductionEvents.length > 0 ||
      damageMultiplierEvents.length > 0);

/**
 * Aggregate repeated StatChange events that have the same source, statName, changeAmount, and modifierType.
 * @param events array of StatChange logs
 * @returns an array of aggregated logs
 */
function aggregateStatChangeEvents(events: GameLog[]): { aggregated: GameLog[]; others: GameLog[] } {
  // Store repeated stat changes in a map keyed by (statName, changeAmount, source, modifierType).
  const map = new Map<
    string,
    {
      event: GameLog;
      count: number;
      cumulativeChange: number;
      latestCurrentValue?: number;
      latestMaxValue?: number;
      latestProgressPercent?: number;
    }
  >();

  const others: GameLog[] = [];

  for (const e of events) {
    // Only handle "StatChange" in aggregator, skip resource usage and non-max-stack-refresh stacking sources
    if (e.type !== "StatChange" || 
        e.details?.modifierType === 'ResourceUsage' || 
        (e.details?.isStackingSource && !e.details?.isMaxStackRefresh)) {
      // Everything else => push into "others"
      others.push(e);
      continue; // skip to next event
    }

    const changeAmount = e.details?.changeAmount ?? 0;
    const source = e.details?.source;       
    const statName = e.details?.statName;
    const modifierType = e.details?.modifierType;
    const changeType = e.details?.changeType;
    const isTimeBasedSource = e.details?.isTimeBasedSource || false;
    const isMaxStackRefresh = e.details?.isMaxStackRefresh || false;

    // Special handling for time-based sources
    if (isTimeBasedSource) {
      // For time-based sources:
      // - Only show increases (changeType === 'increase')
      // - Group by source + statName only (ignore changeAmount)
      // - Keep only the latest currentValue
      if (changeType === 'decrease') {
        // Skip removal/decrease events for time-based sources
        continue;
      }

      const key = `timebased_${statName}_${source}`;
      
      if (!map.has(key)) {
        map.set(key, {
          event: e,
          count: 1,
          cumulativeChange: changeAmount,
          latestCurrentValue: e.details?.currentValue ?? e.details?.totalAmount ?? 0,
          latestMaxValue: e.details?.maxValue,
          latestProgressPercent: e.details?.progressPercent,
        });
      } else {
        const record = map.get(key)!;
        record.count++;
        record.cumulativeChange += changeAmount;
        // Update to latest values
        record.latestCurrentValue = e.details?.currentValue ?? e.details?.totalAmount ?? 0;
        record.latestMaxValue = e.details?.maxValue;
        record.latestProgressPercent = e.details?.progressPercent;
        // Update the event reference to the latest one (to get latest timestamp, etc.)
        record.event = e;
      }
    } else {
      // Regular stat change aggregation logic
      const roundedChange = Math.round(changeAmount * 100) / 100; // Round to 2 decimal places
      const key = `${statName}_${roundedChange}_${source}_${modifierType}_${changeType}_${isMaxStackRefresh}`;

      if (!map.has(key)) {
        map.set(key, {
          event: e,
          count: 1,
          cumulativeChange: changeAmount,
        });
      } else {
        const record = map.get(key)!;
        record.count++;
        record.cumulativeChange += changeAmount;
      }
    }
  }

  // Build final aggregated array
  const aggregated: GameLog[] = [];
  for (const { event, count, cumulativeChange, latestCurrentValue, latestMaxValue, latestProgressPercent } of map.values()) {
    const isTimeBasedSource = event.details?.isTimeBasedSource || false;
    
    if (count === 1 && !isTimeBasedSource) {
      // If only 1 regular event => just keep the original event unmodified
      aggregated.push(event);
    } else {
      // Create an aggregated event
      const aggregatedEvent: GameLog = {
        ...event,
        // unique ID
        id: event.id + "_aggregated",
        details: {
          ...event.details,
          tickCount: count,
          // For time-based sources, use latestCurrentValue; otherwise use cumulative or original
          totalAmount: isTimeBasedSource 
            ? latestCurrentValue 
            : (event.details?.isMaxStackRefresh ? event.details.totalAmount : cumulativeChange),
          currentValue: isTimeBasedSource ? latestCurrentValue : event.details?.currentValue,
          maxValue: isTimeBasedSource ? latestMaxValue : event.details?.maxValue,
          progressPercent: isTimeBasedSource ? latestProgressPercent : event.details?.progressPercent,
          // Keep original changeAmount for individual display in tooltip
          // Preserve flags
          isMaxStackRefresh: event.details?.isMaxStackRefresh || false,
          isTimeBasedSource: isTimeBasedSource,
        },
        type: "StatChange",
      };
      aggregated.push(aggregatedEvent);
    }
  }

  return { aggregated, others };
}

/**
 * Aggregate repeated DamageTaken events that have the same source, damageSubType, and damageAmount.
 * @param events array of DamageTaken logs
 * @returns an array of aggregated logs
 */

//todo: possibly look into rounding but good enough for now. maybe add a flag like isCumulative / isAggregated
function aggregateDamageEvents(events: GameLog[]): { aggregated: GameLog[]; others: GameLog[] } {
  // Store repeated hits in a map keyed by (roundedDamage, source, damageSubType).
  const map = new Map<
    string,
    {
      event: GameLog;
      count: number;
      cumulativeDamage: number;
    }
  >();

  const others: GameLog[] = [];

  for (const e of events) {
    // Only handle "DamageTaken" in aggregator
    if (e.type !== "DamageTaken") {
      // Everything else (e.g. ShieldMitigation, etc.) => push into "others"
      others.push(e);
      continue; // skip to next event
    }

    // aggregator logic for "DamageTaken"
    const dmg = e.details?.damageAmount ?? 0;
    const roundedDmg = Math.round(dmg); 
    const source = e.details?.source;       
    const subType = e.details?.damageSubType;  

    // Build grouping key
    const key = `${roundedDmg}_${source}_${subType}`;

    if (!map.has(key)) {
      map.set(key, {
        event: e,
        count: 1,
        cumulativeDamage: dmg,
      });
    } else {
      const record = map.get(key)!;
      record.count++;
      record.cumulativeDamage += dmg;
    }
  }

  // Build final aggregated array
  const aggregated: GameLog[] = [];
  for (const { event, count, cumulativeDamage } of map.values()) {
    if (count === 1) {
      // If only 1 => just keep the original event unmodified
      aggregated.push(event);
    } else {
      // Create an aggregated event
      const aggregatedEvent: GameLog = {
        ...event,
        // unique ID or new ID
        id: event.id + "_aggregated",
        details: {
          ...event.details,
          cumulativeDamage: cumulativeDamage,
          tickCount: count,
          damageAmount: cumulativeDamage
        },
        // optionally keep type: "DamageTaken" or "AggregatedDamage"
        type: "DamageTaken",
      };
      aggregated.push(aggregatedEvent);
    }
  }

  return { aggregated, others };
}


/**
 * Aggregate repeated StackUpdate events that have the same stackName, currentStacks, maxStacks, and targetId.
 * @param events array of StackUpdate logs
 * @returns an array of aggregated logs
 */
function aggregateStackUpdateEvents(events: GameLog[]): { aggregated: GameLog[]; others: GameLog[] } {
  // Store repeated stack updates in a map keyed by (stackName, currentStacks, maxStacks, targetId).
  const map = new Map<
    string,
    {
      event: GameLog;
      count: number;
    }
  >();

  const others: GameLog[] = [];

  for (const e of events) {
    // Only handle "StackUpdate" events
    if (e.type !== "StackUpdate") {
      others.push(e);
      continue;
    }

    // Use 'source' field (which is what the backend sends) or fallback to 'stackName'
    const stackName = e.details?.source || e.details?.stackName || '';
    const sourceType = e.details?.sourceType || '';
    const currentStacks = e.details?.currentStacks ?? 0;
    const maxStacks = e.details?.maxStacks ?? 0;
    const targetId = e.details?.targetId || '';
    const imageState = e.imageState || '';

    // Build grouping key - include sourceType and imageState to differentiate between different sources
    const key = `${stackName}_${sourceType}_${imageState}_${currentStacks}_${maxStacks}_${targetId}`;

    if (!map.has(key)) {
      map.set(key, {
        event: e,
        count: 1,
      });
    } else {
      const record = map.get(key)!;
      record.count++;
    }
  }

  // Build final aggregated array
  const aggregated: GameLog[] = [];
  for (const { event, count } of map.values()) {
    if (count === 1) {
      // If only 1 => just keep the original event unmodified
      aggregated.push(event);
    } else {
      // Create an aggregated event
      const aggregatedEvent: GameLog = {
        ...event,
        // unique ID
        id: event.id + "_aggregated",
        details: {
          ...event.details,
          tickCount: count,
        },
        type: "AggregatedStackUpdate",
      };
      aggregated.push(aggregatedEvent);
    }
  }

  return { aggregated, others };
}

const { aggregated, others } = aggregateDamageEvents(damageEvents);

// 2) Combine them => finalDamage includes both aggregated lumps + unmodified "others"
//    If you want them all shown in the "Damage Breakdown," you can do:
const finalDamageEvents = [...aggregated, ...others];

// 3) Sort them: DamageMitigation events first, then DamageTaken events, then by timestamp within each type
finalDamageEvents.sort((a, b) => {
  // Priority order: DamageMitigation > DamageTaken > other types
  const getTypePriority = (type: string) => {
    switch (type) {
      case 'DamageMitigation': return 0;
      case 'DamageTaken': return 1;
      default: return 2;
    }
  };

  const aPriority = getTypePriority(a.type);
  const bPriority = getTypePriority(b.type);

  // If different priorities, sort by priority
  if (aPriority !== bPriority) {
    return aPriority - bPriority;
  }

  // If same priority, sort by timestamp
  return a.timestamp - b.timestamp;
});


return (
  <div className="kpg-keypress-group">
    <div className="kpg-keypress-header">
      <KeyPressLog
        log={keyPress}
        spellImageUrls={spellImageUrls}
        AutoAttackImage={AutoAttackImage}
      />
    </div>

    {showDetails && (
      <>
        {/* 1) Possibly show total damage if damageEvents exist */}
        {damageEvents.length > 0 && (
          <>
            <TotalDamage 
              logs={damageEvents} 
              targetMaxHealth={targetMaxHealth || 1000}
            />
          </>
        )}

        {/* 1b) Show duration if this is a recast ability with showDurationInUI flag */}
        {keyPress.details?.showDurationInUI && keyPress.details?.initialCastTimestamp && (
          <DurationDisplay 
            initialCastTimestamp={keyPress.details.initialCastTimestamp}
            currentTimestamp={keyPress.timestamp}
          />
        )}

        {/* 2) Damage breakdown */}
        {finalDamageEvents.length > 0 && (
          <>
            <div className="kpg-divider"></div>
{/*             <div className="kpg-section-title">Damage</div>
 */}            <div className="kpg-related-events">
              {finalDamageEvents.map((event) => {
                // If event.details.tickCount>1 => aggregated
                if (event.details?.tickCount && event.details.tickCount > 1) {
                  return (
                    <RenderAggregatedDamageEvent key={event.id} log={event} />
                  );
                } else {
                  // Normal single event => pass to LogEntry
                  return <LogEntry key={event.id} log={event} />;
                }
              })}
            </div>
          </>
        )}

          {/* 2b) Incoming/Reflected Damage (damage dealt back to the caster) */}
          {incomingDamageEvents && incomingDamageEvents.length > 0 && (
            <>
              <div className="kpg-divider"></div>
              <div className="kpg-incoming-damage-section">
                <div className="kpg-champion-header">
                  <img
                    src={casterImage}
                    alt={championName}
                    className="kpg-champion-image-ally"
                  />
                </div>
                <span className="kpg-incoming-damage-label">Damage Taken</span>
                <div className="kpg-related-events">
                  {(() => {
                    const { aggregated, others } = aggregateDamageEvents(incomingDamageEvents);
                    const finalEvents = [...aggregated, ...others].sort((a, b) => a.timestamp - b.timestamp);
                    return finalEvents.map((event) => {
                      if (event.details?.tickCount && event.details.tickCount > 1) {
                        return <RenderAggregatedDamageEvent key={event.id} log={event} />;
                      } else {
                        return <LogEntry key={event.id} log={event} />;
                      }
                    });
                  })()}
                </div>
              </div>
            </>
          )}

          {/* 3) Target Events */}
          {hasTargetEvents && (
            <>
              <div className="kpg-divider"></div>
              {/* Group all target events by champion */}
              {Array.from(new Set([
                ...Object.keys(targetStatEventsByChampion),
                ...Object.keys(targetCCEventsByChampion),
                ...Object.keys(targetDebuffEventsByChampion)
              ])).map((championId) => {
                // Get champion name from any available event
                const targetChampionName = 
                  targetStatEventsByChampion[championId]?.[0]?.details?.targetName ||
                  targetStatEventsByChampion[championId]?.[0]?.details?.championName ||
                  targetCCEventsByChampion[championId]?.[0]?.details?.targetChampionName ||
                  targetCCEventsByChampion[championId]?.[0]?.details?.championName ||
                  targetDebuffEventsByChampion[championId]?.[0]?.details?.targetName ||
                  targetDebuffEventsByChampion[championId]?.[0]?.details?.championName ||
                  'Unknown Champion';

                const isAlly = targetChampionName === championName; // Compare target with caster
                const targetAssets = championsAssets[targetChampionName];
                const targetImage = targetAssets?.championImageUrl;

                return (
                  <div key={`target-champion-${championId}`} className="kpg-target-events">
                    <div className="kpg-champion-header">
                      <img
                        src={targetImage}
                        alt={targetChampionName}
                        className={isAlly ? "kpg-champion-image-ally" : "kpg-champion-image-enemy"}
                      />
                    </div>
                    
                    {/* Target Stat Events */}
                    {targetStatEventsByChampion[championId] && (
                      <div className="kpg-stat-events">
                        {(() => {
                          // Aggregate stat change events
                          const { aggregated: aggregatedStats, others: otherStats } = aggregateStatChangeEvents(targetStatEventsByChampion[championId]);
                          
                          // Also apply the existing aggregateRepeatedEvents for resource usage
                          const resourceAggregated = aggregateRepeatedEvents(otherStats);
                          
                          // Combine and sort all events
                          const allEvents = [...aggregatedStats, ...resourceAggregated]
                            .sort((a, b) => {
                              const aIsResourceUsage = a.details?.modifierType === 'ResourceUsage' || a.type === 'AggregatedResourceUsage';
                              const bIsResourceUsage = b.details?.modifierType === 'ResourceUsage' || b.type === 'AggregatedResourceUsage';
                              
                              // Resource usage events go to the bottom
                              if (aIsResourceUsage && !bIsResourceUsage) return 1;
                              if (!aIsResourceUsage && bIsResourceUsage) return -1;
                              
                              // Otherwise, maintain original order by timestamp
                              return a.timestamp - b.timestamp;
                            });
                          
                          return allEvents.map((statChangeLog) => {
                            // Debug logging for resource events
                            if (statChangeLog.details?.modifierType === 'ResourceUsage' || statChangeLog.type === 'AggregatedResourceUsage') {
                              console.log('[DEBUG] Rendering resource event:', {
                                type: statChangeLog.type,
                                modifierType: statChangeLog.details?.modifierType,
                                isAggregated: statChangeLog.type === 'AggregatedResourceUsage'
                              });
                            }
                            
                            // Handle aggregated resource events
                            if (statChangeLog.type === 'AggregatedResourceUsage') {
                              return (
                                <RenderAggregatedResourceEvent key={statChangeLog.id} log={statChangeLog} />
                              );
                            }
                            // Handle all stat change events (non-resource) with the unified renderer
                            else if (statChangeLog.type === 'StatChange' && 
                                     statChangeLog.details?.modifierType !== 'ResourceUsage') {
                              return (
                                <RenderAggregatedStatChangeEvent key={statChangeLog.id} log={statChangeLog} />
                              );
                            } 
                            else {
                              return (
                                <LogEntry key={statChangeLog.id} log={statChangeLog} />
                              );
                            }
                          });
                        })()}
                      </div>
                    )}
                    
                    {/* Target CC Events */}
                    {targetCCEventsByChampion[championId] && (
                      <div className="kpg-cc-events">
                        {aggregateRepeatedEvents(targetCCEventsByChampion[championId]).map((ccEventLog) => (
                          <LogEntry key={ccEventLog.id} log={ccEventLog} />
                        ))}
                      </div>
                    )}
                    
                    {/* Target Debuff Events */}
                    {targetDebuffEventsByChampion[championId] && (
                      <div className="kpg-debuff-events">
                        {targetDebuffEventsByChampion[championId].map((debuffEventLog) => (
                          <LogEntry key={debuffEventLog.id} log={debuffEventLog} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* 4) Caster Events */}
          {hasCasterEvents && (
            <>
              <div className="kpg-divider"></div>
              <div className="kpg-caster-events">
                <div className="kpg-champion-header">
                  <img
                    src={casterImage}
                    alt={championName}
                    className="kpg-champion-image-ally"
                  />
                </div>
                {/* Caster Stat Events */}
                {casterStatEvents.length > 0 && (
                  <div className="kpg-stat-events">
                    {(() => {
                      // Aggregate stat change events
                      const { aggregated: aggregatedStats, others: otherStats } = aggregateStatChangeEvents(casterStatEvents);
                      
                      // Also apply the existing aggregateRepeatedEvents for resource usage
                      const resourceAggregated = aggregateRepeatedEvents(otherStats);
                      
                      // Combine and sort all events
                      const allEvents = [...aggregatedStats, ...resourceAggregated]
                        .sort((a, b) => {
                          const aIsResourceUsage = a.details?.modifierType === 'ResourceUsage' || a.type === 'AggregatedResourceUsage';
                          const bIsResourceUsage = b.details?.modifierType === 'ResourceUsage' || b.type === 'AggregatedResourceUsage';
                          
                          // Resource usage events go to the bottom
                          if (aIsResourceUsage && !bIsResourceUsage) return 1;
                          if (!aIsResourceUsage && bIsResourceUsage) return -1;
                          
                          // Otherwise, maintain original order by timestamp
                          return a.timestamp - b.timestamp;
                        });
                      
                      return allEvents.map((event) => {
                        // Debug logging for caster resource events
                        if (event.details?.modifierType === 'ResourceUsage' || event.type === 'AggregatedResourceUsage') {
                          console.log('[DEBUG] Rendering caster resource event:', {
                            type: event.type,
                            modifierType: event.details?.modifierType,
                            isAggregated: event.type === 'AggregatedResourceUsage'
                          });
                        }
                        
                        // Handle aggregated resource events
                        if (event.type === 'AggregatedResourceUsage') {
                          return (
                            <RenderAggregatedResourceEvent key={event.id} log={event} />
                          );
                        }
                        // Handle all stat change events (non-resource) with the unified renderer
                        else if (event.type === 'StatChange' && 
                                 event.details?.modifierType !== 'ResourceUsage') {
                          return (
                            <RenderAggregatedStatChangeEvent key={event.id} log={event} />
                          );
                        } 
                        else {
                          return (
                            <LogEntry key={event.id} log={event} />
                          );
                        }
                      });
                    })()}
                  </div>
                )}
                {/* Caster CC Events */}
                {casterCCEvents.length > 0 && (
                  <div className="kpg-cc-events">
                    {aggregateRepeatedEvents(casterCCEvents).map((event) => (
                      <LogEntry key={event.id} log={event} />
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* 5) Cooldown Reduction Events (NEW) */}
          {cooldownReductionEvents.length > 0 && (
            <>
              <div className="kpg-divider"></div>
{/*               <div className="kpg-section-title">Cooldown Reduction</div>
 */}              <div className="kpg-cooldown-reduction-list">
                {cooldownReductionEvents.map((event) => (
                  <LogEntry key={event.id} log={event} />
                ))}
              </div>
            </>
          )}

          {/* 6) Damage Multiplier Events (NEW) */}
          {damageMultiplierEvents.length > 0 && (
            <>
              <div className="kpg-divider"></div>
              <div className="kpg-damage-multiplier-list">
                {damageMultiplierEvents.map((event) => (
                  <LogEntry key={event.id} log={event} />
                ))}
              </div>
            </>
          )}

          {/* 7) Stack Events */}
          {showStackingEvents && stackUpdates.length > 0 && (
            <>
              {shouldShowStackDivider && <div className="kpg-divider"></div>}
              <div className="kpg-stack-events">
                {(() => {
                  // Aggregate stack update events
                  const { aggregated: aggregatedStacks, others: otherStacks } = aggregateStackUpdateEvents(stackUpdates);
                  
                  // Combine aggregated and other events
                  const allStackEvents = [...aggregatedStacks, ...otherStacks]
                    .sort((a, b) => a.timestamp - b.timestamp);
                  
                  return allStackEvents.map((event) => (
                    <div key={event.id}>
                      {event.type === 'AggregatedStackUpdate' ? (
                        <AggregatedStackEvent log={event} />
                      ) : (
                        <StackEvent log={event} />
                      )}
                    </div>
                  ));
                })()}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default KeyPressGroup;
